import React, { useState, Fragment } from "react";
import {
  useSearchParams,
  useNavigate,
  Link as RouterLink,
} from "react-router-dom";
import axios from "axios";
import {
  Link,
  Container,
  Box,
  CssBaseline,
  TextField,
  Grid,
  Button,
  Snackbar,
  Alert,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { NOTSIGNEDIN, VERYFING, PURCHASING, PREMIUM } from "./states";
import {
  signUp,
  sendVerificationEmail,
  refreshAuthStatus,
  createCheckoutSession,
} from "./services/authService";
import { User } from "./app";
import {
  BILLING_MODE,
  PREMIUM_PLAN,
  PREMIUM_PRICE_CNY,
  PREMIUM_PURCHASING_BUTTON,
  isSupportedPaidPlan,
  normalizePaidPlan,
} from "./billing";
import { pushDataLayerEvent } from "./analytics";

interface Props {
  handleSignUp: any;
}

const SignUpForm = (props: Props) => {
  return (
    <Container component="main" maxWidth="sm">
      <CssBaseline />
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          fontWeight="fontWeightBold"
        >
          会员注册
        </Typography>
        <Box
          component="form"
          noValidate
          onSubmit={props.handleSignUp}
          sx={{ mt: 3, width: "100%" }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                id="email"
                label="电子邮件地址"
                name="email"
                autoComplete="email"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                name="password"
                label="密码"
                helperText="至少 6 个字符，字母和数字组合"
                type="password"
                id="password"
                autoComplete="new-password"
              />
            </Grid>
          </Grid>
          <Box
            sx={{
              mt: 2,
              display: "flex",
              justifyContent: "center",
              alignItems: "baseline",
            }}
          >
            <FormGroup>
              <FormControlLabel
                name="agree"
                control={<Checkbox />}
                label={
                  <Fragment>
                    同意
                    <Link
                      to="/terms"
                      component={RouterLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      使用条款
                    </Link>
                    和
                    <Link
                      to="/privacy"
                      component={RouterLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      隐私政策
                    </Link>
                  </Fragment>
                }
              />
            </FormGroup>
          </Box>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
          >
            注册
          </Button>
          <Grid container justifyContent="flex-end">
            <Grid item>
              <Link variant="body2" to="/login" component={RouterLink}>
                登录
              </Link>
            </Grid>
          </Grid>
          <Grid container justifyContent="flex-end">
            <Grid item>
              <Link variant="body2" to="/pricing" component={RouterLink}>
                返回方案选择
              </Link>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Container>
  );
};

interface VeryfingProps {
  user: User | null;
  handleResubimt: any;
}

const Veryfing = (props: VeryfingProps) => {
  return (
    <Container component="main" maxWidth="sm">
      <CssBaseline />
      <Typography
        component="h2"
        variant="body1"
        color="text.primary"
        align="center"
        sx={{ mt: 4 }}
      >
        确认邮件已发送至 {props.user?.email}。
      </Typography>
      <Typography
        component="h2"
        variant="body1"
        color="text.primary"
        align="center"
        sx={{ mb: 2 }}
      >
        请点击确认邮件中的链接完成注册。
      </Typography>
      <Typography
        component="h2"
        variant="body1"
        color="text.primary"
        align="center"
        sx={{ mb: 2 }}
      >
        如果收不到邮件，请尝试查看垃圾邮件文件夹。
      </Typography>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "baseline",
        }}
      >
        <Button
          type="submit"
          variant="contained"
          sx={{ mt: 3, mb: 2 }}
          onClick={props.handleResubimt}
        >
          重新发送
        </Button>
      </Box>
    </Container>
  );
};

interface PurchasingProps {
  plan: any;
  user: User | null;
  handleTicket: any;
}

const Purchasing = (props: PurchasingProps) => {
  const [isLoading, setLoading] = useState(false);
  const plan = normalizePaidPlan(props.plan) ?? props.plan;
  const handleSubscribe = async (planType: string) => {
    setLoading(true);
    try {
      const result = await createCheckoutSession(
        planType,
        process.env.REACT_APP_READABLE_URL + "thanks?plan=" + planType,
        process.env.REACT_APP_READABLE_URL + "signup?plan=" + planType,
      );

      if (result.ok && result.url) {
        // GTM カスタムイベント: Stripe決済ページへ遷移
        pushDataLayerEvent("addpayment");
        window.location.assign(result.url);
      } else {
        if (result.error === "billing-not-configured") {
          alert("支付配置尚未完成，请稍后再试。");
        } else if (result.error === "invalid-plan") {
          alert("无法加载方案信息，请重新选择方案。");
        } else {
          alert("An error occurred. Please try again.");
        }
        setLoading(false);
      }
    } catch (error) {
      alert("An error occurred. Please try again.");
      setLoading(false);
    }
  };
  if (plan === "ticket") {
    return (
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <Typography
          component="h2"
          variant="body1"
          color="text.primary"
          align="center"
          sx={{ mt: 4 }}
        >
          {props.user?.email} 已验证。
        </Typography>
        <Typography
          component="h2"
          variant="body1"
          color="text.primary"
          align="center"
          sx={{ mt: 1 }}
        >
          请输入激活码。
        </Typography>
        <Typography
          component="h2"
          variant="body1"
          color="text.primary"
          align="center"
          sx={{ mt: 1 }}
        >
          激活码是一个 10 位字母数字代码，如 AB98CDEF7G。
        </Typography>
        <Box
          component="form"
          noValidate
          onSubmit={props.handleTicket}
          sx={{ mt: 2 }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                id="ticket"
                label="激活码"
                name="ticket"
                sx={{ mt: 2, mb: 1 }}
              />
            </Grid>
          </Grid>
          <Button
            fullWidth
            type="submit"
            variant="contained"
            sx={{ mt: 1, mb: 1 }}
          >
            提交
          </Button>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "baseline",
          }}
        >
          <Button
            variant="text"
            sx={{ mt: 1, mb: 2 }}
            to="/pricing"
            component={RouterLink}
          >
            使用在线支付购买
          </Button>
        </Box>
      </Container>
    );
  }
  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <Typography
        component="h2"
        variant="body1"
        color="text.primary"
        align="center"
        sx={{ mt: 4 }}
      >
        {props.user?.email} 已验证。
      </Typography>
      {BILLING_MODE === "subscription" && plan === "monthly" && (
        <Typography
          component="h2"
          variant="body1"
          color="text.primary"
          align="center"
          sx={{ mt: 1 }}
        >
          您已选择月付方案。
        </Typography>
      )}
      {BILLING_MODE === "subscription" && plan === "yearly" && (
        <Typography
          component="h2"
          variant="body1"
          color="text.primary"
          align="center"
          sx={{ mt: 1 }}
        >
          您已选择年付方案。
        </Typography>
      )}
      {BILLING_MODE === "one_time" && plan === PREMIUM_PLAN && (
        <Fragment>
          <Typography
            component="h2"
            variant="body1"
            color="text.primary"
            align="center"
            sx={{ mt: 1 }}
          >
            您已选择 {PREMIUM_PRICE_CNY} 元 1 年 Readable Pro。
          </Typography>
          <Typography
            component="h2"
            variant="body1"
            color="text.secondary"
            align="center"
            sx={{ mt: 1 }}
          >
            支付完成后立即开通，1 年后到期，不会自动续费。
          </Typography>
        </Fragment>
      )}
      {!isSupportedPaidPlan(plan) && (
        <Typography
          component="h2"
          variant="body1"
          color="text.primary"
          align="center"
          sx={{ mt: 1 }}
        >
          无法加载方案信息，请重新选择方案。
        </Typography>
      )}
      {isSupportedPaidPlan(plan) && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "baseline",
          }}
        >
          <Button
            type="submit"
            variant="contained"
            sx={{ mt: 4, mb: 1 }}
            onClick={() => {
              handleSubscribe(plan);
            }}
          >
            {PREMIUM_PURCHASING_BUTTON}
          </Button>
        </Box>
      )}
      {isLoading && (
        <Fragment>
          <Backdrop
            sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={isLoading}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        </Fragment>
      )}
    </Container>
  );
};

const Premium = () => {
  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <Typography
        component="h2"
        variant="body1"
        color="text.primary"
        align="center"
        sx={{ mt: 4 }}
      >
        您已注册
      </Typography>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "baseline",
        }}
      >
        <Button
          variant="contained"
          to="/translate"
          sx={{ mt: 4, width: 200, height: 40 }}
          component={RouterLink}
        >
          立即翻译
        </Button>
      </Box>
    </Container>
  );
};

interface SignUpProps {
  user: User | null;
  signInStatus: number;
  setSignInStatus: any;
  setExpire: any;
  onSignUpSuccess: () => void;
  onRefreshStatus: () => void;
}

const SignUp = (props: SignUpProps) => {
  const [showSnack, setShowSnack] = useState(false);
  const [snackSeverity, setSnackSeverity] = useState("error");
  const [snackMessage, setSnackMessage] = useState<any>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawPlan = searchParams.get("plan");
  const plan = normalizePaidPlan(rawPlan) ?? rawPlan;
  const continuePlan = plan ?? PREMIUM_PLAN;
  const handleSignUp = async (e: any) => {
    e.preventDefault();
    if (!e.target.agree.checked) {
      setSnackMessage("请先同意使用条款");
      setSnackSeverity("error");
      setShowSnack(true);
      return;
    }
    const email = e.target.email.value;
    const password = e.target.password.value;

    const result = await signUp(email, password);

    if (result.ok) {
      // 登録成功、確認メールを送信
      await sendVerificationEmail(
        email,
        process.env.REACT_APP_READABLE_URL + "signup?plan=" + continuePlan,
      );
      // 認証状態を更新
      props.onSignUpSuccess();
      pushDataLayerEvent("signup");
    } else if (result.error === "invalid-email") {
      setSnackMessage("邮箱地址格式不正确");
      setSnackSeverity("error");
      setShowSnack(true);
    } else if (result.error === "weak-password") {
      setSnackMessage("密码强度不足");
      setSnackSeverity("error");
      setShowSnack(true);
    } else if (result.error === "email-already-in-use") {
      setSnackMessage("该邮箱已被注册，请直接登录");
      setSnackSeverity("error");
      setShowSnack(true);
    } else {
      setSnackMessage("注册失败，请检查您输入的信息是否正确。");
      setSnackSeverity("error");
      setShowSnack(true);
    }
  };
  const handleResubimt = async () => {
    if (props.user) {
      // サーバーで最新のメール確認状態を取得
      const status = await refreshAuthStatus();
      if (status.emailVerified) {
        setSnackMessage("邮箱已验证");
        setSnackSeverity("error");
        setShowSnack(true);
        props.onRefreshStatus();
      } else {
        try {
          await sendVerificationEmail(
            props.user.email,
            process.env.REACT_APP_READABLE_URL + "signup?plan=" + continuePlan,
          );
          setSnackMessage("已重新发送确认邮件。");
          setSnackSeverity("info");
          setShowSnack(true);
        } catch (e: any) {
          setSnackMessage("发生错误");
          setSnackSeverity("error");
          setShowSnack(true);
        }
      }
    }
  };
  const handleTicket = (e: any) => {
    e.preventDefault();
    const ticket = e.target.ticket.value;
    axios
      .post(process.env.REACT_APP_READABLE_BACKEND_URL + "ticketauth/", {
        ticket: ticket,
        uid: props.user?.uid,
      })
      .then((response) => {
        if (response.status !== 200) {
          setSnackMessage("出现意外错误。");
          setSnackSeverity("error");
          setShowSnack(true);
        } else if ("invalid" in response.data) {
          setSnackMessage(
            "激活码不正确。激活码是一个10位字母数字代码，如 AB98CDEF7G。请检查您输入的代码是否正确，如问题仍然存在，请联系我们。",
          );
          setSnackSeverity("error");
          setShowSnack(true);
        } else if ("used" in response.data) {
          setSnackMessage("该激活码已被使用。如有疑问，请联系我们。");
          setSnackSeverity("error");
          setShowSnack(true);
        } else if ("ok" in response.data) {
          props.setSignInStatus(PREMIUM);
          props.setExpire(response.data.expire);
          navigate("/thanks?plan=ticket");
        } else {
          setSnackMessage("出现意外错误。");
          setSnackSeverity("error");
          setShowSnack(true);
        }
      })
      .catch((e) => {
        setSnackMessage("出现意外错误。");
        setSnackSeverity("error");
        setShowSnack(true);
      });
  };
  return (
    <Fragment>
      {props.signInStatus === NOTSIGNEDIN && (
        <SignUpForm handleSignUp={handleSignUp} />
      )}
      {props.signInStatus === VERYFING && (
        <Veryfing user={props.user} handleResubimt={handleResubimt} />
      )}
      {props.signInStatus === PURCHASING && (
        <Purchasing user={props.user} plan={plan} handleTicket={handleTicket} />
      )}
      {props.signInStatus === PREMIUM && <Premium />}
      <Snackbar
        open={showSnack}
        autoHideDuration={30000}
        onClose={() => setShowSnack(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowSnack(false)}
          // @ts-ignore
          severity={snackSeverity}
          sx={{ width: "100%" }}
        >
          {snackMessage}
        </Alert>
      </Snackbar>
    </Fragment>
  );
};

export default SignUp;
